// Trivia Night sounds — short WebAudio-synthesised blips (no audio files, no
// copyrighted assets): countdown ticks, the buzz when you lock in, correct /
// wrong stings, a question "ding" and a final fanfare. The AudioContext is
// created lazily and resumed on use (browsers allow it after any tap). A mute
// toggle is remembered per viewer.

import { registerRecordingTap } from '../../../utils/recordingAudio';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
// The Record button mixes this in (utils/recordingAudio).
registerRecordingTap(() => (ctx && master ? { ctx, node: master } : null));
const MUTE_KEY = 'gx-trivia-muted';

let muted = (() => { try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; } })();
const listeners = new Set<(m: boolean) => void>();

export function isMuted() { return muted; }
export function setMuted(m: boolean) {
  muted = m;
  try { if (m) localStorage.setItem(MUTE_KEY, '1'); else localStorage.removeItem(MUTE_KEY); } catch { /* storage blocked */ }
  listeners.forEach((fn) => fn(m));
}
export function onMuteChange(fn: (m: boolean) => void) { listeners.add(fn); return () => { listeners.delete(fn); }; }

function audio(): AudioContext | null {
  if (muted || typeof window === 'undefined') return null;
  try {
    if (!ctx) {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AC) return null;
      ctx = new AC() as AudioContext;
      master = ctx.createGain();
      master.gain.value = 0.32;
      master.connect(ctx.destination);
    }
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
    return ctx;
  } catch { return null; }
}

function tone(freq: number, dur: number, opts: { type?: OscillatorType; at?: number; gain?: number; slideTo?: number; attack?: number } = {}) {
  const a = audio();
  if (!a || !master) return;
  const t0 = a.currentTime + (opts.at ?? 0);
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = opts.type ?? 'sine';
  osc.frequency.setValueAtTime(freq, t0);
  if (opts.slideTo) osc.frequency.exponentialRampToValueAtTime(opts.slideTo, t0 + dur);
  const peak = opts.gain ?? 0.5;
  const attack = opts.attack ?? 0.005;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

export const sfx = {
  /** Countdown tick (last seconds); `final` = the last one, higher. */
  tick(final = false) { tone(final ? 1320 : 990, 0.06, { type: 'square', gain: 0.18 }); },
  /** You locked in an answer. */
  buzz() { tone(150, 0.22, { type: 'sawtooth', gain: 0.35, slideTo: 120 }); tone(300, 0.18, { type: 'square', gain: 0.12 }); },
  /** A new question is up. */
  ding() { tone(880, 0.25, { gain: 0.35 }); tone(1320, 0.35, { gain: 0.25, at: 0.09 }); },
  /** Your answer was right. */
  correct() { [523, 659, 784, 1047].forEach((f, i) => tone(f, 0.18, { type: 'triangle', gain: 0.4, at: i * 0.07 })); },
  /** Your answer was wrong. */
  wrong() { tone(311, 0.22, { type: 'sawtooth', gain: 0.25 }); tone(233, 0.35, { type: 'sawtooth', gain: 0.25, at: 0.16 }); },
  /** Reveal for someone who didn't answer (spectators, the host). */
  reveal() { tone(660, 0.16, { type: 'triangle', gain: 0.3 }); tone(990, 0.24, { type: 'triangle', gain: 0.3, at: 0.1 }); },
  /** Final podium. */
  fanfare() {
    const notes: [number, number, number][] = [[523, 0, 0.14], [659, 0.14, 0.14], [784, 0.28, 0.14], [1047, 0.42, 0.5], [784, 0.62, 0.14], [1047, 0.76, 0.7]];
    notes.forEach(([f, at, d]) => tone(f, d, { type: 'triangle', gain: 0.38, at }));
  },
};
